import React, { FC, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { SCUINoData } from '@/atomic/atoms';
import { ProductDetailServiceQuery } from '@/services/query';
import { ProductSizeInfoGroup } from './ProductSizeInfo.types';
import { PSI } from './productSizeInfo.classes';
import { extractPlmCode, mapStyleMeasurementResponse } from './productSizeInfo.mapper';

interface ProductSizeInfoProps {
    specialCode?: string;
}

const formatMetric = (value: number): string => value.toFixed(2);

const headerCellSx = {
    fontWeight: 700,
    fontSize: '0.8125rem',
    color: '#374151',
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
    py: 1.25,
    px: 2,
    whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #E5E7EB',
    py: 1.25,
    px: 2,
};

export const ProductSizeInfo: FC<ProductSizeInfoProps> = ({ specialCode }) => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState<ProductSizeInfoGroup[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

    useEffect(() => {
        if (!specialCode) {
            setData(null);
            setExpandedPanel(false);
            return;
        }

        let isCancelled = false;

        const fetchProductSizeInfo = async () => {
            setLoading(true);

            try {
                const componentResponse: any = await ProductDetailServiceQuery.GetComponentInformationBySpecialCode({
                    specialCode,
                });
                const plmCode = extractPlmCode(componentResponse);

                if (!plmCode) {
                    if (!isCancelled) {
                        setData(null);
                        setExpandedPanel(false);
                    }
                    return;
                }

                const measurementResponse: any = await ProductDetailServiceQuery.GetStyleMeasurementByPlmCode({
                    PlmCode: plmCode,
                });
                const mappedData = mapStyleMeasurementResponse(measurementResponse);

                if (!isCancelled) {
                    setData(mappedData.length ? mappedData : null);
                    setExpandedPanel(mappedData.length ? `size-${mappedData[0].sizeId}` : false);
                }
            } catch (error) {
                console.error('Product size info fetch failed:', error);
                if (!isCancelled) {
                    setData(null);
                    setExpandedPanel(false);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        fetchProductSizeInfo();

        return () => {
            isCancelled = true;
        };
    }, [specialCode]);

    const isTurkish = i18n.language?.startsWith('tr');

    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 2, py: 4 }}>
                <CircularProgress size={24} />
                <Typography sx={{ color: '#6B7280' }}>{t('general.loading')}</Typography>
            </Box>
        );
    }

    if (!data?.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <SCUINoData message={t('general.noDataFound')} />
            </Box>
        );
    }

    return (
        <Box className={PSI.root}>
            {data.map((sizeGroup) => {
                const panelId = `size-${sizeGroup.sizeId}`;

                return (
                    <Accordion
                        key={sizeGroup.sizeId}
                        className={PSI.accordion}
                        expanded={expandedPanel === panelId}
                        onChange={handleAccordionChange(panelId)}
                        disableGutters
                        elevation={0}
                        sx={{
                            mb: 2,
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            className={PSI.accordionSummary}
                            expandIcon={<ExpandMoreIcon sx={{ color: '#6B7280' }} />}
                            sx={{
                                px: 2,
                                minHeight: 48,
                                backgroundColor: '#FFFFFF',
                                '& .MuiAccordionSummary-content': { my: 1 },
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                                {sizeGroup.sizeName}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={PSI.accordionDetails} sx={{ p: 0 }}>
                            <Box className={PSI.tableWrap} sx={{ overflowX: 'auto' }}>
                                <Table className={PSI.table} size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    ...headerCellSx,
                                                    textAlign: 'left',
                                                    borderTopLeftRadius: '8px',
                                                }}
                                            >
                                                {t('productDetail.sizeInfoTable.measurementName')}
                                            </TableCell>
                                            <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>
                                                {t('productDetail.sizeInfoTable.measurement')}
                                            </TableCell>
                                            <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>
                                                {t('productDetail.sizeInfoTable.toleranceMinus')}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...headerCellSx,
                                                    textAlign: 'right',
                                                    borderTopRightRadius: '8px',
                                                }}
                                            >
                                                {t('productDetail.sizeInfoTable.tolerancePlus')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sizeGroup.measurements.map((measurement) => {
                                            const pomName = isTurkish ? measurement.pomNameTr : measurement.pomNameEn;

                                            return (
                                                <TableRow key={measurement.styleMeasurementPOMId}>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'left' }}>
                                                        {pomName.trim().toUpperCase()}
                                                    </TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                                                        {formatMetric(measurement.gradeMeasMetric)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                                                        {formatMetric(measurement.toleranceMinusMetric)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                                                        {formatMetric(measurement.tolerancePlusMetric)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};
